import * as React from 'react';

import { Link } from '@circleci/compass';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import {
  faTwitter,
  faGithub,
  faLinkedinIn,
  faTwitch,
} from '@fortawesome/free-brands-svg-icons';
import { faRss } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Locales } from '~/contexts/LibContext';
import { styled } from '~/styled';

import { CircleCILogo } from '../Header/CircleCILogo';
import translations from './translations.json';

const FooterWrapper = styled.div(({ theme }) => {
  return `
  width: 100%;
  background: ${theme.colors.white};
  @media (max-width: ${theme.breakpoints[1]}) {
    margin-left: 0;
    width: 100%;
  }
  `;
});

const FooterRow = styled.div(({ theme }) => {
  return `
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  border-top: ${theme.borderWidths.thick} solid ${theme.colors.n700};
  padding: ${theme.space.space24} 0 ${theme.space.space28};
  @media (min-width: 992px) {
    padding: ${theme.space.space16} 0;
  }
  &:first-of-type {
    border-top: none;
    @media (max-width: ${theme.breakpoints[1]}) {
      flex-direction: column;
      flex-wrap: nowrap;
      align-items: center;
    }
  }
  `;
});

const FooterLinksWrapper = styled.nav(({ theme }) => {
  return `
  @media (max-width: ${theme.breakpoints[2]}) {
    flex-grow: 1;
    text-align: center;
  }
  `;
});

const FooterLinksContainer = styled.ul(({ theme }) => {
  return `
  margin: 0 -${theme.space.space12};
  padding: 0;
  list-style: none;
  @media (min-width: ${theme.breakpoints[1]}) {
    margin-right: 0;
  }
  li {
    display: inline-block;
    margin 0 ${theme.space.space12} ${theme.space.space4};
  }
  `;
});

const StyledFooterLink = styled(Link)(({ theme }) => {
  return `
    color: ${theme.colors.n700};
    font-weight: ${theme.fontWeights.medium};
  `;
});

const CopyRight = styled.div(({ theme }) => {
  return `
  margin-bottom: ${theme.space.space4};
  color: ${theme.colors.n400};
  @media (max-width: ${theme.breakpoints[2]}) {
    margin-top: ${theme.space.space16};
    text-align: center;
    flex-grow: 1;
  }
  `;
});

const SocialIconRow = styled.div(({ theme }) => {
  return `
  margin-left: -${theme.space.space16};
  margin-right: -${theme.space.space16};
  padding: 0 ${theme.space.space16} 0 ${theme.space.space16};
  `;
});

const Icons = styled.div(({ theme }) => {
  return `
  font-size: ${theme.fontSizes.fontsize24};
  line-height: ${theme.space.space24};
  padding-top: ${theme.space.space8};
  `;
});

const SocialIcon = styled(FontAwesomeIcon)`
  width: 25px;
  height: auto;
`;

const CustomExternalLinks = styled(Link)(({ theme }) => {
  return `
    margin-left: 20px;
    color: ${theme.colors.n700};
  `;
});

interface IconProps {
  href: string;
  icon: IconDefinition;
  alt: string;
}
const footerSocialIconData: IconProps[] = [
  {
    href: 'https://circleci.com/blog/feed.xml',
    icon: faRss,
    alt: 'CircleCI RSS feed',
  },
  {
    href: 'https://twitter.com/circleci',
    icon: faTwitter,
    alt: 'CircleCI Twitter',
  },
  {
    href: 'https://github.com/circleci',
    icon: faGithub,
    alt: 'CircleCI GitHub',
  },
  {
    href: 'https://twitch.tv/circleci',
    icon: faTwitch,
    alt: 'CircleCI Twitch',
  },
  {
    href: 'https://www.linkedin.com/company/circleci',
    icon: faLinkedinIn,
    alt: 'CircleCI LinkedIn',
  },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const locale = 'en';

  return (
    <FooterWrapper>
      <FooterRow>
        <CircleCILogo data-testid={`footer-logo`} />
        <SocialIconRow>
          <Icons>
            {footerSocialIconData.map(({ href, icon, alt }, i) => {
              return (
                <CustomExternalLinks
                  key={`footer-${icon}-${i}`}
                  href={href}
                  data-testid={`footer-media-${i}`}
                >
                  <SocialIcon icon={icon} title={alt} aria-hidden="true" />
                </CustomExternalLinks>
              );
            })}
          </Icons>
        </SocialIconRow>
      </FooterRow>
      <FooterRow>
        <FooterLinksWrapper>
          <FooterLinksContainer>
            {translations[locale as Locales].map((link, index) => {
              return (
                <li key={index}>
                  <StyledFooterLink
                    href={link.href}
                    data-testid={`footer-policy-${index}`}
                  >
                    {link.text}
                  </StyledFooterLink>
                </li>
              );
            })}
          </FooterLinksContainer>
        </FooterLinksWrapper>
        <CopyRight data-testid="current-year">
          {`© ${currentYear} Circle Internet Services, Inc., All Rights Reserved.`}
        </CopyRight>
      </FooterRow>
    </FooterWrapper>
  );
};
